import { useState, useEffect } from 'react';
import { clients as clientsApi } from '../api/client.js';
import { CheckIcon, ClockIcon, AlertIcon, CameraIcon, SignatureIcon, CalendarIcon } from '../common/icons.jsx';
import FnuciBadge from '../common/FnuciBadge.jsx';

const SCAN_LABELS = {
  ocr_auto: 'OCR auto',
  ocr_rotated: 'OCR rotation',
  manual: 'Manuel',
  ocr_ds3: 'OCR + DS3',
  manual_photo: 'Photo manuelle',
};

const STATUS_LABELS = {
  delivered: 'Livré',
  problem: 'Problème',
  skipped: 'Ignoré',
  pending: 'En attente',
  arrived: 'Arrivé',
  in_progress: 'En cours',
};

const APPT_LABELS = {
  scheduled: 'Planifié',
  completed: 'Terminé',
  no_show: 'Absent',
  cancelled: 'Annulé',
};

/**
 * Delivery summary card for a delivered (or processed) client.
 * Shows stop info, scans, signature, photo, appointment.
 * @param {{ clientId: string }} props
 */
export default function DeliverySummary({ clientId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSignature, setShowSignature] = useState(false);

  useEffect(() => {
    clientsApi.getDeliverySummary(clientId)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [clientId]);

  if (loading) return <p className="text-[#64748b] text-sm py-2">Chargement du récapitulatif...</p>;
  if (!data || (!data.stop && data.items.length === 0)) return null;

  const { stop, signature, items, appointment } = data;

  return (
    <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
      <div className="bg-gradient-to-r from-[#059669] to-emerald-500 px-4 py-3">
        <h3 className="text-white font-semibold text-sm flex items-center gap-2">
          <CheckIcon size={16} />
          Récapitulatif de livraison
        </h3>
      </div>

      <div className="p-4 space-y-4">
        {/* Stop info */}
        {stop && (
          <div className="space-y-2">
            <div className="flex flex-wrap gap-2 text-xs">
              <span className={`px-2.5 py-1 rounded-full font-medium ${
                stop.status === 'delivered' ? 'bg-emerald-100 text-emerald-700' :
                stop.status === 'problem' ? 'bg-red-100 text-red-700' :
                'bg-gray-100 text-gray-600'
              }`}>
                {STATUS_LABELS[stop.status] || stop.status}
              </span>
              {stop.planDate && (
                <span className="flex items-center gap-1 text-[#64748b] bg-gray-50 px-2.5 py-1 rounded-full">
                  <CalendarIcon size={11} />
                  {new Date(stop.planDate).toLocaleDateString('fr-FR')}
                </span>
              )}
              {stop.driverName && (
                <span className="text-[#64748b] bg-gray-50 px-2.5 py-1 rounded-full">
                  {stop.driverName}
                </span>
              )}
            </div>
            {(stop.actualArrival || stop.actualDeparture) && (
              <div className="flex gap-4 text-xs text-[#64748b]">
                {stop.actualArrival && (
                  <span className="flex items-center gap-1">
                    <ClockIcon size={11} />
                    Arrivée : {new Date(stop.actualArrival).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                )}
                {stop.actualDeparture && (
                  <span className="flex items-center gap-1">
                    <ClockIcon size={11} />
                    Départ : {new Date(stop.actualDeparture).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                )}
              </div>
            )}
            {stop.problemType && (
              <div className="flex items-start gap-2 text-xs text-[#dc2626] bg-red-50 rounded-lg px-3 py-2">
                <AlertIcon size={14} className="mt-0.5 shrink-0" />
                <span>{stop.problemType}{stop.problemNote ? ` — ${stop.problemNote}` : ''}</span>
              </div>
            )}
          </div>
        )}

        {/* Scanned items */}
        {items.length > 0 && (
          <div>
            <h4 className="text-xs font-semibold text-[#64748b] uppercase tracking-wider mb-2">
              Unités scannées ({items.length})
            </h4>
            <div className="space-y-1.5">
              {items.map((item) => (
                <div key={item.serial_number} className="flex items-center justify-between text-xs bg-gray-50 rounded-lg px-3 py-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="font-mono font-medium text-[#0f172a]">{item.serial_number}</span>
                    {item.secondary_id && (
                      <span className="text-[#64748b]">({item.secondary_id})</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {item.scan_method && (
                      <span className="text-[#64748b]">{SCAN_LABELS[item.scan_method] || item.scan_method}</span>
                    )}
                    {item.scan_confidence != null && (
                      <span className={`font-medium ${item.scan_confidence >= 0.8 ? 'text-[#059669]' : item.scan_confidence >= 0.5 ? 'text-[#f97316]' : 'text-[#dc2626]'}`}>
                        {Math.round(item.scan_confidence * 100)}%
                      </span>
                    )}
                    <span className={`px-1.5 py-0.5 rounded ${
                      item.status === 'delivered' ? 'bg-emerald-100 text-emerald-700' :
                      item.status === 'problem' ? 'bg-red-100 text-red-700' :
                      'bg-gray-200 text-gray-600'
                    }`}>
                      {item.status === 'delivered' ? 'OK' : item.status}
                    </span>
                    <FnuciBadge status={item.fnuci_status} lastError={item.fnuci_last_error} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Delivery photo */}
        {stop?.photoUrl && (
          <div>
            <h4 className="text-xs font-semibold text-[#64748b] uppercase tracking-wider mb-2 flex items-center gap-1">
              <CameraIcon size={12} /> Photo de livraison
            </h4>
            <img src={stop.photoUrl} alt="Photo de livraison"
              className="w-full max-h-48 object-cover rounded-xl border border-gray-200" />
          </div>
        )}

        {/* Signature */}
        {signature && (
          <div>
            <h4 className="text-xs font-semibold text-[#64748b] uppercase tracking-wider mb-2 flex items-center gap-1">
              <SignatureIcon size={12} /> Signature
            </h4>
            <button
              onClick={() => setShowSignature(!showSignature)}
              className="text-xs text-[#1e40af] hover:underline mb-1"
            >
              {showSignature ? 'Masquer' : 'Afficher'} la signature
            </button>
            {showSignature && (
              <div className="bg-gray-50 rounded-xl p-3 border border-gray-200">
                <img src={signature.signature_data} alt="Signature"
                  className="max-h-32 mx-auto" />
                <div className="flex justify-between items-center mt-2 text-[10px] text-[#64748b]">
                  {signature.signer_name && <span>Signé par : {signature.signer_name}</span>}
                  {signature.signed_at && (
                    <span>{new Date(signature.signed_at).toLocaleString('fr-FR', {
                      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
                    })}</span>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Appointment */}
        {appointment && (
          <div className="flex items-center gap-2 text-xs bg-blue-50 rounded-lg px-3 py-2">
            <CalendarIcon size={14} className="text-[#1e40af] shrink-0" />
            <span className="text-[#0f172a]">
              RDV contrôle : {new Date(appointment.scheduled_date).toLocaleDateString('fr-FR')}
              {' — '}
              <span className="font-medium">{APPT_LABELS[appointment.status] || appointment.status}</span>
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
